import L from "leaflet";

export interface PermalinkState {
  center: L.LatLng;
  zoom: number;
  activeLayers: { id: string; opacity: number }[];
  swipe: [string, string] | null;
}

/** Read state from hash. `baseId` may still appear in old links — caller handles. */
export interface ReadPermalink extends Partial<PermalinkState> {
  /** Legacy: base layer id from pre-merge schema. Caller activates as a normal layer. */
  baseId?: string;
}

export function readPermalink(): ReadPermalink {
  // Prefer the hash (canonical), but fall back to the query string. Browsers'
  // address-bar autocomplete sometimes drops the #fragment when a pasted URL
  // is "completed" to a cached bare origin — the query string survives that.
  // If both are present the hash wins.
  const hashStr = window.location.hash.slice(1);
  const p = new URLSearchParams(
    hashStr || window.location.search.slice(1)
  );
  const out: ReadPermalink = {};
  const ll = p.get("ll");
  if (ll) {
    const [lat, lng, z] = ll.split(",").map(Number);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      out.center = L.latLng(lat, lng);
      if (Number.isFinite(z)) out.zoom = z;
    }
  }
  const base = p.get("base");
  if (base) out.baseId = base;
  // `layers=` ABSENT (null) leaves activeLayers undefined — the caller treats
  // that as "don't touch layers" (view-only hash). An explicitly empty value
  // (`layers=`) yields [] = "deactivate everything". The distinction matters
  // for hashchange handling in main.ts.
  const layers = p.get("layers");
  if (layers !== null) {
    out.activeLayers = layers.split(",").filter(Boolean).map((entry) => {
      const [id, op] = entry.split(":");
      // Opacity is user-editable URL text — clamp junk to a sane [0,1].
      const parsed = op ? Number(op) : 1;
      const opacity = Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : 1;
      return { id, opacity };
    });
  }
  const swipe = p.get("swipe");
  if (swipe) {
    const [a, b] = swipe.split(",");
    if (a && b) out.swipe = [a, b];
  }
  return out;
}

export function writePermalink(state: PermalinkState): void {
  const p = new URLSearchParams();
  p.set("ll", `${state.center.lat.toFixed(5)},${state.center.lng.toFixed(5)},${state.zoom}`);
  if (state.activeLayers.length) {
    p.set(
      "layers",
      state.activeLayers.map((l) => `${l.id}:${l.opacity.toFixed(2)}`).join(",")
    );
  }
  if (state.swipe) p.set("swipe", state.swipe.join(","));
  const hash = "#" + p.toString();
  if (hash !== window.location.hash) {
    history.replaceState(null, "", hash);
  }
}
